import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Star, Send, Navigation } from 'lucide-react';
import { WhatsAppIcon } from './WhatsAppButton';

const DIRECTIONS_URL = 'https://www.google.com/maps/dir/?api=1&destination=Şabanoğlu+75.+Sk+No+11+Tekkeköy+Samsun';
const WHATSAPP_URL = 'https://wa.me/905412665154?text=Merhaba,%20bilgi%20almak%20istiyorum';

const easeLuxury = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: easeLuxury } },
};

const INFO_CARDS = [
  {
    icon: MapPin,
    title: 'ADRES',
    content: (
      <>Şabanoğlu, 75. Sk. No: 11/1<br />55330 Tekkeköy/Samsun, Türkiye</>
    ),
  },
  {
    icon: Phone,
    title: 'TELEFON',
    content: (
      <a href="tel:+905412665154" className="hover:text-accent-gold transition-colors duration-300 font-medium">
        +90 541 266 51 54
      </a>
    ),
  },
  {
    icon: Clock,
    title: 'ÇALIŞMA SAATLERİ',
    content: <>Pazartesi - Cumartesi: 08:00 - 18:00<br />Pazar: Kapalı</>,
  },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: 'Seçiniz...',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // EmailJS entegrasyonu için: emailjs.com üzerinden SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY alıp
      // emailjs.send(SERVICE_ID, TEMPLATE_ID, { from_name, from_phone, service_type, message }) çağrısı eklenebilir.
      setSubmitMessage('✅ Randevu talebiniz alındı! En kısa zamanda sizinle iletişime geçeceğiz.');
      setFormData({ name: '', phone: '', service: 'Seçiniz...', message: '' });
    } catch (error) {
      setSubmitMessage('❌ Bir hata oluştu. Lütfen telefon numarasını kontrol edin.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  return (
    <section id="contact" className="bg-bg-primary py-20 sm:py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7, ease: easeLuxury }}
          className="mb-10 sm:mb-16 space-y-3 sm:space-y-4"
        >
          <p className="text-accent-gold font-semibold tracking-[0.3em] uppercase text-sm">İletişim &amp; Randevu</p>
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-black text-white tracking-tightest">Bize Ulaşın</h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Info */}
          <motion.div
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-100px' }}
            className="space-y-4 sm:space-y-5"
          >
            {INFO_CARDS.map(({ icon: Icon, title, content }) => (
              <motion.div
                key={title}
                variants={fadeUp}
                className="flex items-start gap-4 sm:gap-5 bg-white/5 backdrop-blur-xl border border-border-soft rounded-2xl p-5 sm:p-7 hover:border-accent-gold/40 transition-colors duration-500"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white/5 border border-border-soft flex items-center justify-center flex-shrink-0">
                  <Icon className="text-accent-gold" size={22} />
                </div>
                <div>
                  <h4 className="font-bold text-white tracking-wide mb-1.5 sm:mb-2 text-sm sm:text-base">{title}</h4>
                  <p className="text-text-muted font-light leading-relaxed text-sm sm:text-base">{content}</p>
                </div>
              </motion.div>
            ))}

            <motion.div
              variants={fadeUp}
              className="bg-white/5 backdrop-blur-xl border border-accent-gold/30 rounded-2xl p-5 sm:p-7"
            >
              <div className="flex items-center gap-2 mb-3">
                <Star size={20} className="text-accent-gold fill-accent-gold" />
                <p className="font-bold text-white">5.0/5 &middot; 8 Müşteri Yorumu</p>
              </div>
              <p className="text-text-muted font-light italic">
                "Kalite ve hizmet, dürüst, işinin erbabı, güvenilir..."
              </p>
            </motion.div>

            <motion.div
              variants={fadeUp}
              className="bg-white/5 backdrop-blur-xl border border-border-soft rounded-2xl overflow-hidden"
            >
              <iframe
                title="Öncü Boya Konum"
                src="https://www.google.com/maps?q=Şabanoğlu,+75.+Sk.+No:+11/1,+55330+Tekkeköy/Samsun&output=embed"
                width="100%"
                height="280"
                style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) contrast(85%)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
              />
              <div className="p-5 sm:p-6 flex flex-col sm:flex-row gap-3">
                <a
                  href={DIRECTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-white/5 border border-accent-gold/40 text-accent-gold font-bold py-3 sm:py-3.5 rounded-lg hover:bg-accent-gold hover:text-bg-primary transition-all duration-300"
                >
                  <Navigation size={18} />
                  YOL TARİFİ AL
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold py-3 sm:py-3.5 rounded-lg hover:brightness-110 hover:scale-[1.02] transition-all duration-300"
                >
                  <WhatsAppIcon size={18} />
                  WhatsApp'tan Yaz
                </a>
              </div>
            </motion.div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.7, ease: easeLuxury, delay: 0.1 }}
            className="bg-white/5 backdrop-blur-xl border border-border-soft rounded-2xl p-6 sm:p-8 lg:p-10"
          >
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-6 sm:mb-8">Randevu Talep Formu</h3>

            <form onSubmit={handleFormSubmit} className="space-y-5 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-text-muted mb-3">Adınız Soyadınız *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                  placeholder="Örn: Ali Yılmaz"
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-white/5 border border-border-soft rounded-lg text-white text-sm sm:text-base placeholder:text-white/30 focus:outline-none focus:border-accent-gold focus:shadow-[0_0_0_4px_rgba(201,169,97,0.12)] transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-muted mb-3">Telefon Numarası *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  required
                  placeholder="+90 5XX XXX XX XX"
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-white/5 border border-border-soft rounded-lg text-white text-sm sm:text-base placeholder:text-white/30 focus:outline-none focus:border-accent-gold focus:shadow-[0_0_0_4px_rgba(201,169,97,0.12)] transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-muted mb-3">Hizmet Tipi *</label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleFormChange}
                  required
                  className="w-full px-5 py-4 bg-white/5 border border-border-soft rounded-lg text-white focus:outline-none focus:border-accent-gold focus:shadow-[0_0_0_4px_rgba(201,169,97,0.12)] transition-all duration-300"
                >
                  <option className="bg-bg-secondary">Seçiniz...</option>
                  <option className="bg-bg-secondary">Oto Boyama</option>
                  <option className="bg-bg-secondary">Boya Ürünü Satın Alma</option>
                  <option className="bg-bg-secondary">Teknik Danışmanlık</option>
                  <option className="bg-bg-secondary">Diğer</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-muted mb-3">Mesajınız</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  rows="5"
                  placeholder="İşiniz hakkında kısa bilgi veriniz..."
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-white/5 border border-border-soft rounded-lg text-white text-sm sm:text-base placeholder:text-white/30 focus:outline-none focus:border-accent-gold focus:shadow-[0_0_0_4px_rgba(201,169,97,0.12)] transition-all duration-300 resize-none"
                ></textarea>
              </div>

              {submitMessage && (
                <div className={`p-4 rounded-lg font-medium text-center border ${
                  submitMessage.includes('✅')
                    ? 'bg-green-500/10 text-green-400 border-green-500/30'
                    : 'bg-red-500/10 text-red-400 border-red-500/30'
                }`}>
                  {submitMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent-red text-white font-bold py-3.5 sm:py-4 rounded-lg hover:scale-[1.02] hover:shadow-2xl hover:shadow-accent-red/30 transition-all duration-500 disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2 text-base sm:text-lg"
              >
                {isSubmitting ? 'Gönderiliyor...' : <><Send size={18} /> RANDEVU TALEBİ GÖNDER</>}
              </button>
            </form>

            <p className="text-xs text-text-muted text-center mt-5 sm:mt-6">
              * İşaretli alanlar zorunludur. Form gönderildiğinde sizinle iletişime geçeceğiz.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
